import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { ReadingProgressBar } from "@/components/site/ReadingProgressBar";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------------- Table of contents ---------------- */

const TOC = [
  { id: "market-has-grown", title: "The Market Has Grown Past the Point of Being a Niche" },
  { id: "settlement-volume", title: "Settlement Volume Is Becoming Large Enough to Monitor" },
  { id: "regulators-defined-rules", title: "Regulators Are Replacing Uncertainty With Defined Rules" },
  { id: "mainstream-payment-companies", title: "Mainstream Payment Companies Are Building on Stablecoin Rails" },
  { id: "business-pain-point", title: "The Underlying Business Pain Point Has Not Gone Away" },
  { id: "what-this-means", title: "What This Means for Finance Teams" },
];

/* ---------------- Shared typographic atoms ---------------- */

const P = ({ children, className = "" }) => (
  <p className={`mt-5 text-base leading-[1.75] text-muted-foreground ${className}`}>{children}</p>
);

const Note = ({ children, testId }) => (
  <div
    className="mt-6 rounded-xl border-l-[3px] border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/[0.05] px-6 py-5"
    data-testid={testId}
  >
    <p className="text-[0.95rem] leading-relaxed text-foreground/80">{children}</p>
  </div>
);

const Section = ({ id, index, title, children }) => (
  <section id={id} className="scroll-mt-28 pt-14 first:pt-0" data-testid={`article-section-${id}`}>
    <div className="flex items-baseline gap-4">
      <span className="text-sm font-medium tracking-[0.08em] text-[hsl(174_86%_24%)]">
        {String(index).padStart(2, "0")}
      </span>
      <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.7rem]">
        {title}
      </h2>
    </div>
    <div className="mt-1 border-t border-[hsl(var(--primary))]/10 pt-1">{children}</div>
  </section>
);

/* ---------------- Hero ---------------- */

const ArticleHero = () => (
  <section data-testid="article-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="article-breadcrumb">
        <Link
          to="/resources"
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="article-breadcrumb-resources-link"
        >
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-foreground/80">Why Finance Teams Are Paying Attention to Stablecoins</span>
      </nav>

      {/* Hero panel */}
      <div
        className="mt-10 rounded-3xl bg-[hsl(var(--primary))]/[0.05] px-[14px] py-[33px] sm:px-[38px] sm:py-[49px] lg:px-[54px] lg:py-[65px]"
        data-testid="article-hero-panel"
      >
        <h1 className="font-serif-editorial max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Why Finance Teams Are Paying Attention to Stablecoins
        </h1>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="flex items-center gap-2.5">
            <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
              Article
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            4 min read
          </span>
        </div>
        <div className="mt-8 max-w-4xl lg:max-w-[1080px]">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            A few years ago, stablecoins were mostly a conversation among
            crypto traders. Today, the conversation has moved into finance
            departments, treasury teams, and accounting firms. Several
            developments are driving that shift. Together, they explain why
            stablecoins have become part of the conversation around modern
            business payments.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="article-hero-tags">
          {["Stablecoins", "Payments"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-[hsl(var(--primary))]/25 px-4 py-1.5 text-sm font-medium text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Sticky table of contents with scrollspy ---------------- */

const TocSidebar = () => {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = TOC[0].id;
      for (const item of TOC) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <aside className="hidden lg:block">
      <nav
        className="sticky top-24 rounded-2xl bg-card/80 p-7 px-[23px] shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
        aria-label="Table of contents"
        data-testid="article-toc"
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Table of Contents
        </h2>
        <ol className="mt-5 space-y-1">
          {TOC.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block rounded-md py-1.5 pr-2 text-sm leading-snug transition-colors hover:text-[hsl(174_86%_24%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeId === s.id
                    ? "font-medium text-[hsl(174_86%_24%)]"
                    : "text-muted-foreground"
                }`}
                data-testid={`article-toc-link-${s.id}`}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
};

/* ---------------- Article body ---------------- */

const ArticleBody = () => (
  <section data-testid="article-body-section">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
        <TocSidebar />

        <article className="max-w-[76ch]" data-testid="article-content">
          {/* 01 — Market growth */}
          <Section id="market-has-grown" index={1} title="The Market Has Grown Past the Point of Being a Niche">
            <P>
              According to Transak's stablecoin market cap analysis, total
              stablecoin market capitalization stood at roughly $316 billion in
              mid-2026, up from $27 billion at the end of 2020.
            </P>
            <P>
              The size of the market matters less than what that growth
              represents. As stablecoins move into mainstream business use,
              they become more likely to intersect with existing accounting
              systems. Understanding how they work early makes it easier to
              build the right processes before transaction volumes grow.
            </P>
          </Section>

          {/* 02 — Settlement volume */}
          <Section id="settlement-volume" index={2} title={<>Settlement Volume Is Becoming Large Enough{" "}<br className="hidden lg:block" />to Monitor</>}>
            <P>
              Monthly stablecoin settlement volume reached $7.2 trillion in
              February 2026, compared with $6.8 trillion processed by the US
              ACH network over the same period, based on Artemis data cited by
              Forbes.
            </P>
            <P>
              The comparison should be interpreted carefully: stablecoin
              settlement volume includes exchange activity and on-chain
              transfers, while ACH primarily represents domestic payments.
              However, the scale demonstrates that blockchain-based settlement
              has become significant enough for financial institutions and
              businesses to monitor.
            </P>
            <Note testId="article-note-scale">
              At this scale, stablecoin transactions become relevant to
              everyday financial operations. Existing controls and reporting
              processes should be able to support this type of activity if it
              appears.
            </Note>
          </Section>

          {/* 03 — Regulation */}
          <Section id="regulators-defined-rules" index={3} title={<>Regulators Are Replacing Uncertainty With{" "}<br className="hidden lg:block" />Defined Rules</>}>
            <P>
              Uncertainty has historically been the main reason finance and
              compliance teams stayed cautious about stablecoins. That is
              changing on both sides of the Atlantic, in ways that are specific
              enough to plan around.
            </P>
            <P>
              In the European Union, MiCA's stablecoin provisions have applied
              since June 2024, with the wider framework for crypto-asset
              service providers in force since December 2024 (European Banking
              Authority). MiCA splits stablecoins into two categories:
            </P>
            <ul className="mt-4 space-y-2.5 pl-1">
              {[
                "e-money tokens (EMTs), linked to a single currency such as the euro;",
                "asset-referenced tokens (ARTs), linked to multiple currencies or assets.",
              ].map((li) => (
                <li key={li} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                  <span className="mt-[0.72em] h-[3px] w-3 shrink-0 bg-[hsl(var(--accent))]" aria-hidden="true" />
                  {li}
                </li>
              ))}
            </ul>
            <P>
              EMT issuers must be authorized as a credit institution or e-money
              institution and must hold reserves equal to 100% of outstanding
              tokens; ART issuers face a minimum 30% segregated-reserve
              requirement. Both categories require a regulator-approved
              disclosure document before a token can be offered to the public.
            </P>
            <P>
              In the United States, the GENIUS Act, signed into law in 2025,
              establishes the first federal licensing framework for payment
              stablecoins, permitting both bank and non-bank issuers, as
              outlined in Spark's overview of stablecoin regulation. It
              requires reserves to be held in cash, central bank deposits, or
              short-dated Treasuries, along with regular disclosure of reserve
              composition.
            </P>
            <Note testId="article-note-defined-terms">
              The practical value lies in the fact that "reserve requirement,"
              "disclosure," and "authorized issuer" are now defined terms with
              specific thresholds rather than open questions. That makes it
              easier to build a due diligence checklist for evaluating a
              particular stablecoin instead of relying on general assurances
              from a vendor.
            </Note>
          </Section>

          {/* 04 — Mainstream payment companies */}
          <Section id="mainstream-payment-companies" index={4} title={<>Mainstream Payment Companies Are Building{" "}<br className="hidden lg:block" />on Stablecoin Rails</>}>
            <P>
              Stablecoins are increasingly being integrated by the same
              companies that already run traditional payment infrastructure.
              Stripe reintroduced crypto payments in 2024, PayPal's PYUSD has
              been integrated into checkout flows, and Visa has been piloting
              stablecoin settlement on its own network, per Startup Fortune's
              reporting.
            </P>
            <P>
              As providers expand their payment infrastructure, stablecoins
              become another payment method that businesses may encounter
              alongside traditional options.
            </P>
          </Section>

          {/* 05 — Business pain point */}
          <Section id="business-pain-point" index={5} title={<>The Underlying Business Pain Point Has Not{" "}<br className="hidden lg:block" />Gone Away</>}>
            <P>
              Much of the interest still traces back to a concrete, familiar
              problem: cross-border payments remain slow and expensive.
            </P>
            <P>
              Cross-border remittances cost an average of 6.49% globally as of
              early 2025, and among businesses that have already used
              stablecoins for payments, 41% report cost savings of 10% or more,
              mostly on cross-border transactions — figures compiled in Reap's
              2026 stablecoin statistics.
            </P>
            <P>
              Those potential savings are one reason many businesses are
              evaluating stablecoins for cross-border payment workflows.
            </P>
          </Section>

          {/* 06 — What this means */}
          <Section id="what-this-means" index={6} title="What This Means for Finance Teams">
            <P>
              None of these trends mean stablecoins are about to replace
              existing payment infrastructure. The same data shows that most
              stablecoin volume today is still trading and on-chain activity
              rather than real-economy payments. But market scale, settlement
              volume, regulatory clarity, and adoption by mainstream payment
              companies are reinforcing each other at the same time, which is
              different from a single hype cycle passing through.
            </P>
            <Note testId="article-note-practical-understanding">
              Finance teams don't need to adopt stablecoins right away. They do
              need a practical understanding of how stablecoin transactions
              become financial records, what reserve backing means, and how
              these transactions may be treated for tax purposes.
            </Note>
            <P>
              That knowledge makes it easier to evaluate stablecoins if they
              become relevant{" "}
              <br className="hidden lg:block" />
              to the business.
            </P>
          </Section>
        </article>
      </div>
    </Container>
  </section>
);

/* ---------------- Final CTA ---------------- */

const ArticleFinalCta = () => (
  <section data-testid="article-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Build Practical Knowledge of Digital Finance
          </h2>
          <div className="mx-auto max-w-2xl lg:max-w-[880px]">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              As stablecoins become part of business finance, finance teams
              need more than market updates. They need{" "}
              <br className="hidden lg:block" />
              a practical understanding of digital payments, accounting
              workflows, reporting requirements, and financial controls. WFII
              programs help accountants, finance professionals, treasury teams,
              and business leaders{" "}
              <br className="hidden lg:block" />
              build that knowledge.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="article-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Page ---------------- */

export default function ArticleWhyStablecoinsPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <ReadingProgressBar />
        <main className="wfii-longform flex-1 space-y-[60px] lg:space-y-[100px]">
          <ArticleHero />
          <ArticleBody />
          <ArticleFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
